import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Settings as SettingsIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { GmailConnectionCard, GmailConnectionsAdmin, LocationSettings, MapsAppSettings } from '../components/settings';

export const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin } = useAuthStore();

  // Handle Gmail OAuth callback query params
  useEffect(() => {
    const gmailResult = searchParams.get('gmail');
    const calendarResult = searchParams.get('calendar');

    if (!gmailResult && !calendarResult) return;

    if (gmailResult) {
      switch (gmailResult) {
        case 'connected':
          toast.success('Gmail connected successfully! Email sync will start shortly.');
          break;
        case 'denied':
          toast.error('Gmail connection was denied. Please try again if you want to enable email sync.');
          break;
        case 'error': {
          const reason = searchParams.get('reason');
          const messages: Record<string, string> = {
            invalid_state: 'Connection failed: invalid security token. Please try again.',
            user_not_found: 'Connection failed: user account not found.',
            missing_tokens: 'Connection failed: Google did not provide necessary permissions.',
            no_email: 'Connection failed: could not retrieve your Gmail address.',
            callback_failed: 'Connection failed. Please try again.',
          };
          toast.error(messages[reason || ''] || 'Gmail connection failed. Please try again.');
          break;
        }
      }
    }

    if (calendarResult) {
      switch (calendarResult) {
        case 'enabled':
          toast.success('Google Calendar sync enabled! Scheduled activities will now sync to your calendar.');
          break;
        case 'denied':
          toast.error('Calendar access was not granted. Reconnect Google and leave Calendar ticked to sync scheduled activities.');
          break;
      }
    }

    // Clear the query params so toast doesn't repeat on refresh
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page header */}
      <div className="flex items-center gap-3 mb-8">
        <SettingsIcon className="w-7 h-7 text-gray-600 dark:text-gray-400" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage your integrations and account preferences
          </p>
        </div>
      </div>

      <div className="space-y-8">
        {/* Location section */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Location
          </h2>
          <div className="space-y-4">
            <LocationSettings />
            <MapsAppSettings />
          </div>
        </section>

        {/* Integrations section */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Integrations
          </h2>
          <GmailConnectionCard />
        </section>

        {/* Admin section */}
        {isAdmin() && (
          <section>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Administration
            </h2>
            <GmailConnectionsAdmin />
          </section>
        )}
      </div>
    </div>
  );
};
